import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, MapPin, ShieldCheck, GraduationCap, Scale, ArrowRight, Zap, Cpu, Trophy } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import SeoHead from '@/components/shared/SeoHead';
import PageHero, { HeroAccent } from '@/components/shared/PageHero';
import AdSlot from '@/components/shared/AdSlot';
import AffiliateCta from '@/components/shared/AffiliateCta';
import SearchDropdown from '@/components/search/SearchDropdown';
import { generateSalaryPageUrl } from '@/lib/occupationUtils';
import { HomePageOccupationMap } from '@/components/shared/HomePageOccupationMap';
import HomepageRankingsWidget from '@/components/shared/HomepageRankingsWidget';
import NewsletterSignup from '@/components/shared/NewsletterSignup';
import { generateDatasetSchema } from '@/lib/schema';
import { isAiEconomyOccupation, AI_ECONOMY_STATS } from '@/lib/aiEconomyConfig';

interface TrendingCareer {
  soc_code?: string;
  code?: string;
  title?: string;
  name?: string;
  averageSalary?: number;
  growth?: string;
  growthNumeric?: number;
  employment?: number;
}

const HomePage = () => {
  const navigate = useNavigate();

  // Job discovery first: Planner head terms get the highlight rings.
  // Underpaid stays as a secondary Reddit path, not the homepage's only job.
  const entryPoints = [
    {
      title: 'Highest paying jobs',
      description: 'Rank every occupation by median salary with official BLS wage data.',
      icon: Trophy,
      href: '/rankings/',
      highlight: true,
    },
    {
      title: 'Highest paying jobs without a degree',
      description: "Careers that pay well without requiring a four-year college degree.",
      icon: GraduationCap,
      href: '/no-degree-jobs',
      highlight: true,
    },
    {
      title: 'Careers in the AI economy',
      description: 'The jobs AI is creating - with real salaries and official growth projections.',
      icon: Cpu,
      href: '/ai-economy-jobs',
      highlight: true,
    },
    {
      title: 'Am I underpaid?',
      description: 'Compare your pay to official BLS data and see your percentile in seconds.',
      icon: Scale,
      href: '/am-i-underpaid',
    },
    {
      title: "Jobs AI can't easily replace",
      description: "Get your occupation's automation-risk score and timeline.",
      icon: ShieldCheck,
      href: '/ai-career-analysis',
    },
    {
      title: 'Where your paycheck goes furthest',
      description: 'Real wages adjusted for the local cost of living.',
      icon: MapPin,
      href: '/best-states',
    },
  ];

  const { data: trendingCareers, isLoading: trendingLoading } = useQuery({
    queryKey: ['trending-careers'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-trending-careers');
      if (error) throw error;
      return (data || []) as TrendingCareer[];
    },
    staleTime: 1000 * 60 * 60 * 6,
    gcTime: 1000 * 60 * 60 * 24,
  });

  const [selectedOccupation, setSelectedOccupation] = useState<{ code: string; title: string }>({
    code: '15-1252',
    title: 'Software Developers',
  });
  const [hasManuallyChanged, setHasManuallyChanged] = useState(false);

  useEffect(() => {
    if (!hasManuallyChanged && trendingCareers && trendingCareers.length > 0) {
      const first = trendingCareers[0];
      const next = {
        code: first.soc_code || first.code || '15-1252',
        title: first.title || first.name || 'Software Developers',
      };
      if (next.code !== '15-1252' || next.title !== 'Software Developers') {
        setSelectedOccupation(next);
      }
    }
  }, [trendingCareers, hasManuallyChanged]);

  const handleOccupationChange = (occupation: { code: string; title: string }) => {
    setSelectedOccupation(occupation);
    setHasManuallyChanged(true);
  };

  const featuredCareers: TrendingCareer[] = (trendingCareers && trendingCareers.length > 0)
    ? trendingCareers
    : [
        { name: 'Software Developers', averageSalary: 127260, growth: '+22%', growthNumeric: 22, employment: 1365500 },
        { name: 'Data Scientists', averageSalary: 131490, growth: '+35%', growthNumeric: 35, employment: 113300 },
        { name: 'Nurse Practitioners', averageSalary: 121610, growth: '+52%', growthNumeric: 52, employment: 234000 },
        { name: 'Financial Managers', averageSalary: 139790, growth: '+17%', growthNumeric: 17, employment: 681070 },
      ];

  return (
    <>
      <SeoHead
        title="Wage Atlas - What Does Any Job Really Pay?"
        description="Free official salary data for every job in America. Explore the highest paying jobs and careers that pay well, compare wages by state, and see if you're underpaid - no signup, no paywall."
        canonical="https://wageatlas.com/"
      />
      {selectedOccupation && (
        <script type="application/ld+json">
          {JSON.stringify(generateDatasetSchema(
            `Geographic Salary Analysis for ${selectedOccupation.title}`,
            `Interactive map showing salary data for ${selectedOccupation.title} across all 50 states`,
            'https://wageatlas.com',
            {
              keywords: [selectedOccupation.title, 'salary', 'wages', 'geographic analysis', 'state comparison'],
              about: { name: selectedOccupation.title, occupationalCategory: selectedOccupation.code },
            }
          ))}
        </script>
      )}

      <div className="min-h-screen bg-background">
        {/* Hero: job-salary discovery search; underpaid is a secondary escape hatch */}
        <PageHero
          title={
            <>
              What does any job
              <HeroAccent> really pay?</HeroAccent>
            </>
          }
          titleSize="large"
          subtitle="Choosing or changing careers? Search any job title for official BLS salaries, highest-paying rankings, and real wages by state - free, no signup."
          maxWidth="max-w-2xl"
        >
          <div className="w-full max-w-xl mx-auto min-w-0">
            <SearchDropdown
              placeholder="Search a job title (e.g. registered nurse)"
              size="lg"
            />
            <p className="text-sm text-white/80 mt-3">
              No signup. No paywall. Official BLS &amp; BEA data.
            </p>
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => navigate('/am-i-underpaid')}
              className="mt-5 h-auto min-h-11 w-full max-w-full min-w-0 whitespace-normal px-4 text-center sm:w-auto sm:px-8 border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
            >
              <Scale className="shrink-0" />
              <span className="text-balance">
                Already have a job? Check if you&apos;re underpaid
              </span>
              <ArrowRight className="shrink-0" />
            </Button>
          </div>
        </PageHero>

        {/* Live answer: interactive occupation map */}
        <section id="geographic-analysis" className="py-12 px-2 sm:px-4 bg-muted/20">
          <div className="mx-auto w-full max-w-6xl">
            <HomePageOccupationMap
              selectedOccupation={selectedOccupation}
              onOccupationChange={handleOccupationChange}
            />
          </div>
        </section>

        {/* Rankings widget - contextual to user's selected occupation.
            Match geographic-analysis gutters so card content width aligns. */}
        <section className="py-16 px-2 sm:px-4">
          <div className="mx-auto w-full max-w-6xl">
            <HomepageRankingsWidget selectedOccupation={selectedOccupation} />
          </div>
        </section>

        {/* 3-4 high-value entry points */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold text-center mb-2">Get a straight answer</h2>
            <p className="text-center text-muted-foreground mb-10">
              The questions everyone's asking right now - answered with real data.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {entryPoints.map((ep) => {
                const Icon = ep.icon;
                return (
                  <Card
                    key={ep.title}
                    className={`cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
                      ep.highlight ? 'ring-2 ring-wage-accent bg-gradient-to-br from-wage-accent/5 to-transparent' : ''
                    }`}
                    onClick={() => navigate(ep.href)}
                  >
                    <CardContent>
                      <Icon className="h-9 w-9 text-wage-primary mb-3" />
                      <h3 className="text-lg font-semibold mb-2 flex items-center gap-1">
                        {ep.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">{ep.description}</p>
                      <span className="inline-flex items-center text-sm font-medium text-wage-primary">
                        Explore <ArrowRight className="h-4 w-4 ml-1" />
                      </span>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* AI economy stat strip */}
        <section className="relative overflow-hidden py-14 px-4 border-y border-wage-primary/15 dark:border-wage-primary/30 bg-gradient-to-b from-wage-primary/[0.08] via-wage-primary/[0.04] to-muted/40 dark:from-wage-primary/25 dark:via-wage-primary/10 dark:to-background">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--wage-primary)/0.12),transparent_55%)] dark:bg-[radial-gradient(ellipse_at_top,hsl(var(--wage-primary)/0.28),transparent_60%)]"
          />
          <div className="relative container mx-auto max-w-5xl">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Zap className="h-5 w-5 text-wage-primary" />
              <p className="text-sm uppercase tracking-wide font-medium text-wage-primary/80 dark:text-wage-primary-light">
                The AI economy, in official numbers
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center my-8">
              <div className="rounded-xl border border-wage-primary/10 bg-background/70 dark:bg-background/40 dark:border-wage-primary/20 px-5 py-6 shadow-sm backdrop-blur-sm">
                <div className="text-4xl font-bold text-wage-primary">
                  {Math.round(AI_ECONOMY_STATS.computerMathGrowthPct / AI_ECONOMY_STATS.totalEconomyGrowthPct)}x
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Tech occupations are growing {AI_ECONOMY_STATS.computerMathGrowthPct}% by 2034 - triple the overall economy (BLS)
                </p>
              </div>
              <div className="rounded-xl border border-wage-primary/10 bg-background/70 dark:bg-background/40 dark:border-wage-primary/20 px-5 py-6 shadow-sm backdrop-blur-sm">
                <div className="text-4xl font-bold text-wage-primary">+{AI_ECONOMY_STATS.dataScientistGrowthPct}%</div>
                <p className="text-sm text-muted-foreground mt-2">
                  Projected growth for data scientists - the 4th fastest-growing job in America (BLS)
                </p>
              </div>
              <div className="rounded-xl border border-wage-primary/10 bg-background/70 dark:bg-background/40 dark:border-wage-primary/20 px-5 py-6 shadow-sm backdrop-blur-sm">
                <div className="text-4xl font-bold text-wage-primary">+{AI_ECONOMY_STATS.aiWagePremiumPct}%</div>
                <p className="text-sm text-muted-foreground mt-2">
                  Wage premium for workers with AI skills vs. same-role peers (PwC AI Jobs Barometer)
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild>
                <Link to="/ai-economy-jobs">
                  Explore AI Economy Jobs
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="bg-background/60 dark:bg-background/30 border-wage-primary/20 hover:bg-background/80">
                <Link to="/insights/ai-wage-premium">Read: The AI Wage Premium</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Trending careers + revenue rail */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-2/3">
                <h2 className="text-3xl font-bold mb-8">
                  Trending Career Salaries
                  <span className="text-sm font-normal text-muted-foreground ml-2">Real-time market data</span>
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {trendingLoading
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <Card key={i} className="animate-pulse">
                          <CardContent>
                            <div className="h-5 bg-muted rounded mb-2" />
                            <div className="flex justify-between items-center">
                              <div className="h-8 w-24 bg-muted rounded" />
                              <div className="h-4 w-12 bg-muted rounded" />
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    : featuredCareers.map((career) => (
                        <Card
                          key={career.name || career.title}
                          className="hover:shadow-md transition-shadow cursor-pointer group"
                          onClick={() => navigate(generateSalaryPageUrl({ title: career.title || career.name || '' }, 'united-states'))}
                        >
                          <CardContent>
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <h3 className="font-semibold text-lg group-hover:text-wage-primary transition-colors">
                                {career.name || career.title}
                              </h3>
                              {isAiEconomyOccupation(career.soc_code || career.code, career.name || career.title) && (
                                <Badge
                                  variant="outline"
                                  className="shrink-0 text-xs border-wage-accent/50 text-wage-accent"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('/ai-economy-jobs');
                                  }}
                                >
                                  <Zap className="h-3 w-3 mr-1" />
                                  AI economy
                                </Badge>
                              )}
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-2xl font-bold text-wage-primary">
                                {career.averageSalary ? `$${Math.round(career.averageSalary).toLocaleString()}` : 'N/A'}
                              </span>
                              {career.growth && (
                                <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                                  (career.growthNumeric ?? 0) >= 20 ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' :
                                  (career.growthNumeric ?? 0) >= 10 ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' :
                                  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                                }`}>
                                  {career.growth}
                                </span>
                              )}
                            </div>
                            {career.employment && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {career.employment.toLocaleString()} jobs nationwide
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                </div>
              </div>

              <div className="lg:w-1/3 space-y-6">
                <AdSlot slot="sidebar" />
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-wage-primary" />
                    Find your next opportunity
                  </h3>
                  <AffiliateCta provider="ziprecruiter" type="job-search" />
                  <AffiliateCta provider="coursera" type="training" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section className="py-10 px-4 border-y bg-background">
          <div className="container mx-auto max-w-4xl text-center">
            <p className="text-sm uppercase tracking-wide text-muted-foreground mb-2">Built on official government data</p>
            <p className="text-lg font-medium">
              U.S. Bureau of Labor Statistics (OEWS) &middot; BEA Regional Price Parities &middot; O*NET &middot; BLS Employment Projections
            </p>
          </div>
        </section>

        {/* Newsletter (data-journalism distribution) */}
        <section className="py-12 px-4 bg-muted/30">
          <div className="container mx-auto max-w-4xl">
            <NewsletterSignup
              source="homepage"
              title="The State of American Wages"
              description="Quarterly wage reports and the data behind them - straight to your inbox."
            />
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePage;
